import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { slugify, readingTime, buildExcerpt, toDate } from '../utils/articleUtils';

const COLLECTION = 'articles';
const articlesCol = () => collection(db, COLLECTION);

// Sort helper (client-side) so we don't require Firestore composite indexes.
const byNewest = (a, b) => {
  const at = (toDate(a.publishedAt) || toDate(a.updatedAt) || toDate(a.createdAt) || 0);
  const bt = (toDate(b.publishedAt) || toDate(b.updatedAt) || toDate(b.createdAt) || 0);
  return (bt ? bt.getTime() : 0) - (at ? at.getTime() : 0);
};

/** Public: fetch published articles (optionally capped), newest first. */
export async function getPublishedArticles(max) {
  const q = query(articlesCol(), where('status', '==', 'published'));
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byNewest);
  return typeof max === 'number' ? items.slice(0, max) : items;
}

/** Admin: fetch every article (drafts + published). Requires auth. */
export async function getAllArticles() {
  const snap = await getDocs(articlesCol());
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byNewest);
}

/** Public: fetch a single published article by slug. */
export async function getArticleBySlug(slug) {
  // Constrain by status too, otherwise Firestore security rules reject the
  // query for logged-out (public) visitors (the rule can only be satisfied
  // when the query guarantees status == 'published'). Two equality filters
  // are served by single-field indexes, so no composite index is required.
  const q = query(
    articlesCol(),
    where('slug', '==', slug),
    where('status', '==', 'published'),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/** Admin: fetch a single article by id (for editing). */
export async function getArticleById(id) {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

function normalise(input) {
  const content = input.content || '';
  return {
    title: (input.title || '').trim(),
    slug: input.slug ? slugify(input.slug) : slugify(input.title || ''),
    excerpt: (input.excerpt || '').trim() || buildExcerpt(content),
    content,
    coverImage: (input.coverImage || '').trim(),
    tags: Array.isArray(input.tags) ? input.tags : [],
    status: input.status === 'published' ? 'published' : 'draft',
    readMinutes: readingTime(content),
  };
}

/** Admin: create a new article. Returns the new document id. */
export async function createArticle(input) {
  const data = normalise(input);
  const ref = await addDoc(articlesCol(), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt: data.status === 'published' ? serverTimestamp() : null,
  });
  return ref.id;
}

/** Admin: update an existing article. */
export async function updateArticle(id, input, { justPublished = false } = {}) {
  const data = normalise(input);
  const patch = {
    ...data,
    updatedAt: serverTimestamp(),
  };
  if (justPublished) patch.publishedAt = serverTimestamp();
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, patch);
}

/** Admin: toggle publish status quickly. */
export async function setArticleStatus(id, status) {
  const ref = doc(db, COLLECTION, id);
  const patch = { status, updatedAt: serverTimestamp() };
  if (status === 'published') patch.publishedAt = serverTimestamp();
  await updateDoc(ref, patch);
}

/** Admin: delete an article. */
export async function deleteArticle(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}
